/**
 * Poll GET /chat/history while the panel is open so replies a human operator
 * sends from the console reach the visitor. Cadence is `nextPollDelay`
 * (polling.ts); this hook owns the timer, the visibility rule and the
 * response handling.
 *
 * Restarts (and goes hot again) whenever `activityAt` changes — the component
 * bumps it on send, on open and when the tab becomes visible, which is what
 * "resume on the next send/open/visibility" means in practice.
 */
import { useEffect, useRef, type MutableRefObject } from 'react';
import { HOT_POLL_MS, nextPollDelay } from './polling';

/** `status` values on the history response after which there is nothing more to poll for. */
const FINAL_STATUSES = new Set(['closed', 'archived', 'deleted', 'erased']);

export interface HistoryPollingOptions {
  enabled: boolean;
  apiUrl: string;
  tenantId: string;
  sessionId: string | null;
  /** Epoch ms of the visitor's last send / open / return to the tab. */
  activityAt: number;
  /** Newest message SK seen; advanced here, persisted by the component. */
  cursorRef: MutableRefObject<string | null>;
  /** Operator message ids already shown, so a refresh cannot duplicate them. */
  seenAdminIdsRef: MutableRefObject<Set<string>>;
  /** New operator replies, oldest first. */
  onAdminMessages: (contents: string[]) => void;
  /** 403/404: the conversation is not ours any more — stop until the next conversation. */
  onGone: () => void;
  /** The conversation was closed/archived by the operator — stop for good. */
  onClosed: (status: string) => void;
}

export function useHistoryPolling(options: HistoryPollingOptions): void {
  const { enabled, apiUrl, tenantId, sessionId, activityAt, cursorRef, seenAdminIdsRef } = options;

  // Latest callbacks without restarting the timer when the component re-renders.
  const callbacksRef = useRef(options);
  callbacksRef.current = options;
  // When the last request went out, across restarts: a restart within the hot
  // cadence (every send bumps `activityAt`) waits out the remainder instead
  // of polling again at once — otherwise each visitor turn cost one or two
  // extra GET /chat/history on the very stage the back-off is protecting.
  const lastPolledAtRef = useRef(0);
  const lastSessionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !sessionId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let delay = HOT_POLL_MS;

    const poll = async () => {
      lastPolledAtRef.current = Date.now();
      try {
        const params = new URLSearchParams({ tenantId, sessionId });
        if (cursorRef.current) params.set('after', cursorRef.current);
        const res = await fetch(`${apiUrl}/chat/history?${params.toString()}`);
        if (cancelled) return;
        if (res.status === 403 || res.status === 404) {
          callbacksRef.current.onGone();
          return;
        }
        // 429 is the stage throttle, not a fault of this conversation: skip
        // the tick quietly and keep the schedule.
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data || typeof data !== 'object') return;
        if (Array.isArray(data.messages)) {
          const fresh: string[] = [];
          for (const m of data.messages) {
            if (!m || typeof m !== 'object') continue;
            if (typeof m.id === 'string' && (!cursorRef.current || m.id > cursorRef.current)) {
              cursorRef.current = m.id;
            }
            if (
              m.role === 'admin' &&
              typeof m.id === 'string' &&
              typeof m.content === 'string' &&
              !seenAdminIdsRef.current.has(m.id)
            ) {
              seenAdminIdsRef.current.add(m.id);
              fresh.push(m.content);
            }
          }
          if (fresh.length) callbacksRef.current.onAdminMessages(fresh);
        }
        if (typeof data.status === 'string' && FINAL_STATUSES.has(data.status)) {
          callbacksRef.current.onClosed(data.status);
        }
      } catch {
        // Network blip — try again next tick.
      }
    };

    const tick = async () => {
      if (cancelled) return;
      // Never poll a background tab. The component bumps `activityAt` when
      // the tab becomes visible again, which restarts this effect hot.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      await poll();
      if (cancelled) return;
      const next = nextPollDelay(Date.now() - activityAt, delay);
      if (next === null) return;
      delay = next;
      timer = setTimeout(tick, delay);
    };

    // A new conversation has nothing in common with the last request; an
    // activity bump on the same one only needs the schedule to go hot again.
    const sinceLast = Date.now() - lastPolledAtRef.current;
    const firstDelay = lastSessionRef.current === sessionId ? Math.max(0, HOT_POLL_MS - sinceLast) : 0;
    lastSessionRef.current = sessionId;
    if (firstDelay > 0) timer = setTimeout(tick, firstDelay);
    else tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, sessionId, activityAt, apiUrl, tenantId, cursorRef, seenAdminIdsRef]);
}
